/**
 * Trigger domain types — mirror of ``packages/roboto/src/roboto/domain/triggers``.
 *
 * Kept in sync with the SDK wire models by hand. A trigger subscribes to one or
 * more platform events, optionally guards them with a condition over the event
 * namespace, dedupes at a ``once_per`` granularity, and dispatches one or more
 * targets when it fires.
 */

import { type AnalysisScope } from "@/shared/domain/agentThread/AgentThreadRecord";
import { ThreadVisibility } from "@/shared/domain/agentThread/AgentThreadRecord";
import { type Condition } from "@/types";

import {
  type ComputeRequirements,
  type ContainerParameters,
  type InvocationInput,
  type InvocationUploadDestination,
} from "../actions/ActionRecord";

/** Mirror of ``PlatformEventType``: a thing that happens on the platform. */
export enum PlatformEventType {
  FileUploaded = "file.uploaded",
  UploadCompleted = "dataset.upload_completed",
  FileIngested = "file.ingested",
  FileMetadataUpdated = "file.metadata_updated",
  DatasetMetadataUpdated = "dataset.metadata_updated",
  DatasetCreated = "dataset.created",
  DatasetTagAdded = "dataset.tag_added",
  InvocationCompleted = "invocation.completed",
  InvocationFailed = "invocation.failed",
  SessionCreated = "session.created",
  SessionFileAdded = "session.file_added",
  SessionUpdated = "session.updated",
  /** An event, the time-anchored annotation, was created. */
  EventCreated = "event.created",
  /**
   * A trigger's own schedule reached one of its minutes. Not subscribable: a
   * trigger fires on a schedule by declaring a `Schedule` source, and the
   * scheduler delivers this occurrence to that trigger alone.
   */
  ScheduleFired = "schedule.fired",
}

/**
 * Mirror of ``OncePer``: whether a second matching occurrence is the same
 * firing or a new one. `Occurrence` is every time; the rest name the subject
 * that may fire at most once.
 */
export enum OncePer {
  Occurrence = "occurrence",
  /** Once per event, the annotation. */
  Event = "event",
  File = "file",
  Dataset = "dataset",
  Invocation = "invocation",
  Session = "session",
}

/** Mirror of ``TriggerTargetType``: the kind of thing a trigger does when it fires. */
export enum TriggerSourceType {
  Events = "events",
  Schedule = "schedule",
}

/** Fire when any of the subscribed platform events occurs, deduped at `once_per`. */
export interface EventSubscription {
  type: TriggerSourceType.Events;
  events: PlatformEventType[];
  once_per: OncePer;
}

/** Fire on a five-field cron schedule, evaluated in UTC; deduped per scheduled minute. */
export interface Schedule {
  type: TriggerSourceType.Schedule;
  cron: string;
}

/** What makes a trigger fire: an event subscription or a schedule. */
export type TriggerSource = EventSubscription | Schedule;

export function isSchedule(source: TriggerSource): source is Schedule {
  return source.type === TriggerSourceType.Schedule;
}

export function isEventSubscription(
  source: TriggerSource,
): source is EventSubscription {
  return source.type === TriggerSourceType.Events;
}

/** The event types a source fires for; a schedule fires for its own occurrence type. */
export function sourceEventTypes(source: TriggerSource): PlatformEventType[] {
  return isSchedule(source) ? [PlatformEventType.ScheduleFired] : source.events;
}

export enum TriggerTargetType {
  InvokeAction = "invoke_action",
  StartAgent = "start_agent",
  SendSlackMessage = "send_slack_message",
}

/**
 * Mirror of ``ActionReference`` as a target stores it: ``owner`` defaults to
 * the trigger's org and ``digest`` to the action's latest version; the wire
 * carries both as null when unset.
 */
export interface TargetActionReference {
  name: string;
  owner?: string | null;
  digest?: string | null;
}

export interface InvokeActionTarget {
  type: TriggerTargetType.InvokeAction;
  target_id: string;
  action: TargetActionReference;
  required_inputs: string[];
  additional_inputs?: string[] | null;
  /**
   * Query-based input selection resolved when the invocation runs: how a
   * schedule-fired trigger names its inputs, since it fires with no files.
   */
  invocation_input?: InvocationInput | null;
  parameter_values: Record<string, unknown>;
  compute_requirement_overrides?: ComputeRequirements | null;
  container_parameter_overrides?: ContainerParameters | null;
  timeout?: number | null;
  upload_destination?: InvocationUploadDestination | null;
}

export interface StartAgentTarget {
  type: TriggerTargetType.StartAgent;
  target_id: string;
  agent_id: string;
  /** Agent variable name to a template string resolved against the event namespace. */
  values: Record<string, string>;
  visibility: ThreadVisibility;
  analysis_scope?: AnalysisScope | null;
}

export interface SendSlackMessageTarget {
  type: TriggerTargetType.SendSlackMessage;
  target_id: string;
  channel_id: string;
  /** May contain ``{{...}}`` placeholders resolved against the event namespace. */
  text: string;
}

/** Mirror of ``TriggerTargetSpec``, discriminated on ``type``. */
export type TriggerTargetSpec =
  | InvokeActionTarget
  | StartAgentTarget
  | SendSlackMessageTarget;

/**
 * How each target type is named to a person. Lives with the enum rather than
 * with the create form because every surface that shows a target -- the form,
 * the list row, the read view, the dispatch table -- needs the same word for
 * it.
 */
export const TARGET_TYPE_LABELS: Record<TriggerTargetType, string> = {
  [TriggerTargetType.InvokeAction]: "Invoke action",
  [TriggerTargetType.StartAgent]: "Start agent",
  [TriggerTargetType.SendSlackMessage]: "Send Slack message",
};

/** Which engine owns a trigger row during the v1 → v2 migration. */
export type TriggerEngine = "v1" | "v2";

/** Mirror of ``TriggerRecord``: the v2 wire shape every trigger is served in. */
export interface TriggerV2Record {
  trigger_id: string;
  org_id: string;
  name: string;
  enabled: boolean;
  fires_on: TriggerSource;
  condition: Condition | null;
  targets: TriggerTargetSpec[];
  engine: TriggerEngine;
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
}

/** Mirror of ``CreateTriggerRequest``. */
export interface CreateTriggerV2Request {
  name: string;
  fires_on: TriggerSource;
  condition?: Condition | null;
  targets: TriggerTargetSpec[];
  enabled?: boolean;
}

/**
 * Mirror of ``UpdateTriggerRequest``. Omitted fields are left unchanged
 * (the backend uses a NotSet sentinel; JSON omission expresses it).
 * ``condition: null`` explicitly clears the condition.
 */
export interface UpdateTriggerV2Request {
  /** Replaces the whole source: events and once_per change together. */
  fires_on?: TriggerSource;
  condition?: Condition | null;
  targets?: TriggerTargetSpec[];
  enabled?: boolean;
}

/** Mirror of ``TriggerDispatchStatus``: lifecycle state of one dispatch. */
export enum TriggerDispatchStatus {
  Claimed = "claimed",
  Dispatched = "dispatched",
  Failed = "failed",
  Shadow = "shadow",
  Unknown = "unknown",
}

/** Mirror of ``TriggerDispatchRecord``: one attempt to run one target for one matched event. */
export interface TriggerDispatchRecord {
  trigger_id: string;
  idempotency_token: string;
  target_id: string;
  org_id: string;
  target_type: TriggerTargetType;
  event_type: PlatformEventType;
  event_id: string;
  /** The entity the matched platform event was about, as a ``roboto://`` URI. */
  subject: string;
  /** The dataset the subject belongs to; null for a subject with none (a session, an invocation). */
  dataset_id?: string | null;
  status: TriggerDispatchStatus;
  status_detail?: string | null;
  result_ref?: string | null;
  claimed_at: string;
  finalized_at?: string | null;
}

/**
 * Mirror of ``SampleEvent``: one event type, dereferenced. The envelope, every
 * namespace root the type exposes with a full realistic record under it, and
 * the ``root.path`` names a condition field or ``{{ }}`` placeholder may use.
 * Built server-side by the same namespace the evaluator uses, so it is what a
 * template will actually resolve against -- not a hand-written approximation.
 */
export interface SampleEvent {
  event_type: PlatformEventType;
  event: Record<string, unknown>;
  namespace: Record<string, Record<string, unknown>>;
  paths: string[];
}

/** Mirror of ``EventSamplesResponse``: one sample per event type. */
export interface EventSamplesResponse {
  samples: Record<PlatformEventType, SampleEvent>;
}

export { ThreadVisibility };
